/**
 * Collects structured diagnostics from an <audio> element when an error or
 * stall fires, logs them to the console, and POSTs the same payload to the
 * `/api/telemetry/playback-error` endpoint so prod logs capture the same
 * data we'd otherwise have to read from a user's browser screenshot.
 *
 * The signed URL's signing query string is intentionally stripped — only the
 * host is included — so signed URLs never end up in server logs.
 */
const basePath = (() => {
  if (typeof import.meta !== "undefined" && import.meta.env?.BASE_URL) {
    return import.meta.env.BASE_URL.replace(/\/$/, "");
  }
  return "";
})();

export interface PlaybackTelemetryArgs {
  audio: HTMLAudioElement;
  jobId: number | string;
  page: "result" | "status";
  eventType: "error" | "stalled" | "waiting";
  lastRefreshAt?: number | null;
  expiresAt?: number | null;
  getToken: () => Promise<string | null>;
}

function hostOf(src: string | null | undefined): string {
  if (!src) return "";
  try {
    return new URL(src).host;
  } catch {
    return "";
  }
}

export async function reportPlaybackError({
  audio,
  jobId,
  page,
  eventType,
  lastRefreshAt,
  expiresAt,
  getToken,
}: PlaybackTelemetryArgs): Promise<void> {
  const now = Date.now();
  const payload = {
    jobId: typeof jobId === "string" ? Number(jobId) : jobId,
    page,
    eventType,
    errorCode: audio.error?.code ?? null,
    errorMessage: audio.error?.message ?? null,
    networkState: audio.networkState,
    readyState: audio.readyState,
    currentSrcHost: hostOf(audio.currentSrc),
    currentTime: Number.isFinite(audio.currentTime) ? audio.currentTime : 0,
    duration: Number.isFinite(audio.duration) ? audio.duration : 0,
    pageVisible:
      typeof document !== "undefined" ? document.visibilityState === "visible" : true,
    msSinceLastRefresh:
      lastRefreshAt != null ? now - lastRefreshAt : null,
    urlExpiresInMs: expiresAt != null ? expiresAt - now : null,
  };

  // eslint-disable-next-line no-console
  console.error("[playback-error]", payload);

  try {
    const token = await getToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    await fetch(`${basePath}/api/telemetry/playback-error`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Telemetry is best-effort — never let it crash the player.
  }
}
